import {
  BacklinkDisplayType,
  BacklinkDisplayTypeMap,
  ContentFieldToDisplayedDetailTypeMap,
  DisplayedDetailType,
  DisplayedDetailTypeMap,
  DisplayedDetailTypeToContentFieldMap,
  Field,
  FieldJSON,
  FieldType,
  MutableField,
  NumberFormat,
  NumberFormatMap,
  ParentPageType,
  YBacklinkDisplayType,
  YBacklinkDisplayTypeMap,
  YBacklinkDisplayTypePossibleValues,
  YDisplayedDetailType,
  YDisplayedDetailTypeMap,
  YDisplayedDetailTypePossibleValues,
  YNumberFormat,
  YNumberFormatMap,
  YNumberFormatPossibleValues,
} from '@databases/model/field';
import { Op } from 'quill-delta';
import * as Y from 'yjs';
import * as z from 'zod';

import { LazyMapElement } from './helpers/lazy-map-element';
import { SynchronizedMapElement } from './helpers/synchronized-map-element';
import { SynchronizedRecord } from './helpers/synchronized-record';
import { DisposableElement } from './helpers/types';
import { YCustomNumberFormat } from './y-custom-number-format';
import { YSelectOptions } from './y-select-options';

export const Properties = {
  description: 'd',
  name: 'n',
  type: 't',
  width: 'w',
  settingShowDescription: 'sd',
  settingContentField: 'scf',
  settingNewlines: 'sn',
  settingResolve: 'sr',
  settingMulti: 'sm',
  settingAllowEdit: 'sae',
  settingDatabaseId: 'sdi',
  settingBacklinkDisplayType: 'sbdt',
  settingDisplayedDetail: 'sdd',
  settingFieldId: 'sfi',
  settingIncludeVersionComment: 'sivc',
  settingCreatable: 'sc',
  settingEntryFieldField: 'seff',
  settingPageTemplateId: 'spti',
  settingParentPage: 'spp',
  settingParentPageType: 'sppt',
  settingParentPageFieldId: 'sppfi',
  settingJiraField: 'sjf',
  settingJiraServerId: 'sjsi',
  settingCloudId: 'sci',
  settingSelectOptions: 'sso',
  settingNumberFormat: 'snf',
  settingCustomNumberFormat: 'scnf',
  settingDefaultEnabled: 'sde',
};

export enum YFieldType {
  TEXT = 't',
  NUMBER = 'n',
  DATE = 'd',
  SELECT = 's',
  USER = 'u',
  IMAGE = 'i',
  PAGE_LINK = 'pl',
  PAGE_LABELS = 'pla',
  PAGE_STATUS = 'ps',
  PAGE_DETAILS = 'pd',
  HYPERLINK = 'h',
  ENTRY_LINK = 'el',
  ENTRY_DETAILS = 'ed',
  BACKLINK = 'b',
  JIRA_ISSUE = 'ji',
  JIRA_ISSUE_DETAILS = 'jid',
}

export const YFieldMapping: Record<YFieldType, FieldType> = {
  [YFieldType.TEXT]: FieldType.Text,
  [YFieldType.NUMBER]: FieldType.Number,
  [YFieldType.DATE]: FieldType.Date,
  [YFieldType.SELECT]: FieldType.Select,
  [YFieldType.USER]: FieldType.User,
  [YFieldType.IMAGE]: FieldType.Image,
  [YFieldType.PAGE_LINK]: FieldType.PageLink,
  [YFieldType.PAGE_LABELS]: FieldType.PageLabels,
  [YFieldType.PAGE_STATUS]: FieldType.PageStatus,
  [YFieldType.PAGE_DETAILS]: FieldType.PageDetails,
  [YFieldType.HYPERLINK]: FieldType.Hyperlink,
  [YFieldType.ENTRY_LINK]: FieldType.EntryLink,
  [YFieldType.ENTRY_DETAILS]: FieldType.EntryDetails,
  [YFieldType.BACKLINK]: FieldType.Backlink,
  [YFieldType.JIRA_ISSUE]: FieldType.JiraIssue,
  [YFieldType.JIRA_ISSUE_DETAILS]: FieldType.JiraIssueDetails,
};

export const FieldMapping: Record<FieldType, YFieldType> = {
  [FieldType.Text]: YFieldType.TEXT,
  [FieldType.Number]: YFieldType.NUMBER,
  [FieldType.Date]: YFieldType.DATE,
  [FieldType.Select]: YFieldType.SELECT,
  [FieldType.User]: YFieldType.USER,
  [FieldType.Image]: YFieldType.IMAGE,
  [FieldType.PageLink]: YFieldType.PAGE_LINK,
  [FieldType.PageLabels]: YFieldType.PAGE_LABELS,
  [FieldType.PageStatus]: YFieldType.PAGE_STATUS,
  [FieldType.PageDetails]: YFieldType.PAGE_DETAILS,
  [FieldType.Hyperlink]: YFieldType.HYPERLINK,
  [FieldType.EntryLink]: YFieldType.ENTRY_LINK,
  [FieldType.EntryDetails]: YFieldType.ENTRY_DETAILS,
  [FieldType.Backlink]: YFieldType.BACKLINK,
  [FieldType.JiraIssue]: YFieldType.JIRA_ISSUE,
  [FieldType.JiraIssueDetails]: YFieldType.JIRA_ISSUE_DETAILS,
};

export const YFieldTypeValues = Object.values(YFieldType);

export const YPageLocation = z.object({
  type: z.nativeEnum(ParentPageType),
  fieldId: z.optional(z.string()),
});

export class YField
  extends SynchronizedRecord
  implements Field, MutableField, DisposableElement
{
  private nameElement: SynchronizedMapElement<Y.Text>;
  private descriptionElement: SynchronizedMapElement<Y.Text>;
  #customNumberFormat: LazyMapElement<YCustomNumberFormat>;
  #selectOptions: YSelectOptions;

  constructor(data: Y.Map<unknown>) {
    super(data);

    this.#customNumberFormat = new LazyMapElement(
      data,
      Properties.settingCustomNumberFormat,
      (input) => {
        if (input instanceof Y.Map) {
          return new YCustomNumberFormat(input);
        }
      },
      () => new Y.Map(),
      (input, attach) => {
        return new YCustomNumberFormat(input, attach);
      }
    );

    this.#selectOptions = new YSelectOptions(
      new SynchronizedMapElement<Y.Array<unknown>>(
        data,
        Properties.settingSelectOptions,
        (input) => (input instanceof Y.Array ? input : undefined),
        () => new Y.Array()
      )
    );

    this.nameElement = new SynchronizedMapElement(
      data,
      Properties.name,
      (input) => (input instanceof Y.Text ? input : undefined),
      () => new Y.Text()
    );

    this.descriptionElement = new SynchronizedMapElement(
      data,
      Properties.description,
      (input) => (input instanceof Y.Text ? input : undefined),
      () => new Y.Text()
    );
  }

  get description(): string {
    return this.descriptionElement.value?.toString() ?? '';
  }

  applyDescriptionDelta(delta: Op[]): void {
    this.descriptionElement.valueRequired.applyDelta(delta);
  }

  get name(): string {
    return this.nameElement.value?.toString() ?? '';
  }

  applyNameDelta(delta: Op[]): void {
    this.nameElement.valueRequired.applyDelta(delta);
  }

  get type(): FieldType {
    return YFieldMapping[
      this.getStringEnum<typeof YFieldTypeValues>(
        Properties.type,
        YFieldTypeValues,
        YFieldType.TEXT
      )
    ];
  }

  set type(newType: FieldType) {
    this.set(Properties.type, FieldMapping[newType]);
  }

  get showDescription(): boolean {
    return this.getBoolean(Properties.settingShowDescription, false);
  }

  set showDescription(val: boolean) {
    this.set(Properties.settingShowDescription, val);
  }

  get width(): number | undefined {
    return this.getNumber(Properties.width, undefined);
  }

  set width(value: number | undefined) {
    this.set(Properties.width, value);
  }

  get newlines(): boolean {
    return this.getBoolean(Properties.settingNewlines, false);
  }

  set newlines(val: boolean) {
    this.set(Properties.settingNewlines, val);
  }

  get multi(): boolean {
    return this.getBoolean(Properties.settingMulti, false);
  }

  set multi(val: boolean) {
    this.set(Properties.settingMulti, val);
  }

  get resolve(): boolean {
    return this.getBoolean(Properties.settingResolve, true);
  }

  set resolve(val: boolean) {
    this.set(Properties.settingResolve, val);
  }

  get allowEdit(): boolean {
    return this.getBoolean(Properties.settingAllowEdit, true);
  }

  set allowEdit(val: boolean) {
    this.set(Properties.settingAllowEdit, val);
  }

  get databaseId(): string | undefined {
    return this.getString(Properties.settingDatabaseId, undefined);
  }

  set databaseId(val: string | undefined) {
    this.set(Properties.settingDatabaseId, val);
  }

  get contentField(): string | undefined {
    return this.getString(
      Properties.settingContentField,
      DisplayedDetailTypeToContentFieldMap[this.displayedDetail]
    );
  }

  set contentField(val: string | undefined) {
    this.set(Properties.settingContentField, val);
    if (val && Object.keys(ContentFieldToDisplayedDetailTypeMap).includes(val)) {
      const yDisplayedDetailType = ContentFieldToDisplayedDetailTypeMap[val];
      this.set(
        Properties.settingDisplayedDetail,
        DisplayedDetailTypeMap[yDisplayedDetailType]
      );
    }
  }

  get displayedDetail(): DisplayedDetailType {
    const yDisplayedDetailType = this.getStringEnum(
      Properties.settingDisplayedDetail,
      YDisplayedDetailTypePossibleValues,
      YDisplayedDetailType.CREATED_AT
    );

    return YDisplayedDetailTypeMap[yDisplayedDetailType];
  }

  set displayedDetail(val: DisplayedDetailType) {
    const yDisplayedDetailType = DisplayedDetailTypeMap[val];
    this.set(Properties.settingDisplayedDetail, yDisplayedDetailType);
    if (val && Object.keys(DisplayedDetailTypeToContentFieldMap).includes(val)) {
      const contentField = DisplayedDetailTypeToContentFieldMap[val];
      this.set(Properties.settingContentField, contentField);
    }
  }

  get backlinkDisplayType(): BacklinkDisplayType {
    const yBacklinkDisplayType = this.getStringEnum(
      Properties.settingBacklinkDisplayType,
      YBacklinkDisplayTypePossibleValues,
      YBacklinkDisplayType.AS_LINK
    );

    return YBacklinkDisplayTypeMap[yBacklinkDisplayType];
  }

  set backlinkDisplayType(val: BacklinkDisplayType) {
    const yBacklinkDisplayType = BacklinkDisplayTypeMap[val];
    this.set(Properties.settingBacklinkDisplayType, yBacklinkDisplayType);
  }

  get fieldId(): string | undefined {
    return this.getString(Properties.settingFieldId, undefined);
  }

  set fieldId(val: string | undefined) {
    this.set(Properties.settingFieldId, val);
  }

  get includeVersionComment(): boolean | undefined {
    return this.getBoolean(Properties.settingIncludeVersionComment, undefined);
  }

  set includeVersionComment(val: boolean | undefined) {
    this.set(Properties.settingIncludeVersionComment, val);
  }

  get creatable(): boolean | undefined {
    return this.getBoolean(Properties.settingCreatable, undefined);
  }

  set creatable(val: boolean | undefined) {
    this.set(Properties.settingCreatable, val);
  }

  get pageTemplateId(): string | undefined {
    return this.getString(Properties.settingPageTemplateId, undefined);
  }

  set pageTemplateId(val: string | undefined) {
    this.set(Properties.settingPageTemplateId, val);
  }

  get parentPage(): string | undefined {
    return this.getString(Properties.settingParentPage, undefined);
  }

  set parentPage(val: string | undefined) {
    this.set(Properties.settingParentPage, val);
  }

  get parentPageType(): ParentPageType {
    return this.getStringEnum(
      Properties.settingParentPageType,
      Object.values(ParentPageType),
      ParentPageType.PAGE
    );
  }

  set parentPageType(val: ParentPageType) {
    this.set(Properties.settingParentPageType, val);
  }

  get parentPageFieldId(): string | undefined {
    return this.getString(Properties.settingParentPageFieldId, undefined);
  }

  set parentPageFieldId(val: string | undefined) {
    this.set(Properties.settingParentPageFieldId, val);
  }

  get entryFieldField(): string | undefined {
    return this.getString(Properties.settingEntryFieldField, undefined);
  }

  set entryFieldField(val: string | undefined) {
    this.set(Properties.settingEntryFieldField, val);
  }

  get jiraField(): string | undefined {
    return this.getString(Properties.settingJiraField, undefined);
  }

  set jiraField(val: string | undefined) {
    this.set(Properties.settingJiraField, val);
  }

  get jiraServerId(): string | undefined {
    return this.getString(Properties.settingJiraServerId, undefined);
  }

  set jiraServerId(val: string | undefined) {
    this.set(Properties.settingJiraServerId, val);
  }

  get cloudId(): string | undefined {
    return this.getString(Properties.settingCloudId, undefined);
  }

  set cloudId(val: string | undefined) {
    this.set(Properties.settingCloudId, val);
  }

  get numberFormat(): NumberFormat {
    const yNumberFormat = this.getStringEnum(
      Properties.settingNumberFormat,
      YNumberFormatPossibleValues,
      YNumberFormat.NUMBER
    );

    return YNumberFormatMap[yNumberFormat];
  }

  set numberFormat(val: NumberFormat) {
    const yNumberFormat = NumberFormatMap[val];
    this.set(Properties.settingNumberFormat, yNumberFormat);
  }

  get customNumberFormat() {
    return this.#customNumberFormat.value;
  }

  get selectOptions(): YSelectOptions {
    return this.#selectOptions;
  }

  get defaultEnabled(): boolean {
    return this.getBoolean(Properties.settingDefaultEnabled, false);
  }

  set defaultEnabled(val: boolean) {
    this.set(Properties.settingDefaultEnabled, val);
  }

  public toJSON(): FieldJSON {
    return {
      description: this.description,
      showDescription: this.showDescription,
      name: this.name,
      type: this.type,
      width: this.width,
      resolve: this.resolve,
      multi: this.multi,
      newlines: this.newlines,
      allowEdit: this.allowEdit,
      databaseId: this.databaseId,
      displayedDetail: this.displayedDetail,
      contentField: this.contentField,
      backlinkDisplayType: this.backlinkDisplayType,
      fieldId: this.fieldId,
      includeVersionComment: this.includeVersionComment,
      creatable: this.creatable,
      entryFieldField: this.entryFieldField,
      pageTemplateId: this.pageTemplateId,
      parentPage: this.parentPage,
      parentPageType: this.parentPageType,
      parentPageFieldId: this.parentPageFieldId,
      jiraField: this.jiraField,
      jiraServerId: this.jiraServerId,
      cloudId: this.cloudId,
      numberFormat: this.numberFormat,
      customNumberFormat: this.customNumberFormat.toJSON(),
      selectOptions: this.selectOptions.toJSON(),
      defaultEnabled: this.defaultEnabled,
    };
  }

  static fromJSON(data: Pick<FieldJSON, 'type'> & FieldJSON): YField {
    return new YField(
      new Y.Map([
        [Properties.name, new Y.Text(data.name)],
        [Properties.type, FieldMapping[data.type]],
        [Properties.description, new Y.Text(data.description)],
        [Properties.width, data.width],

        [Properties.settingShowDescription, data.showDescription],
        [Properties.settingNewlines, data.newlines],
        [Properties.settingMulti, data.multi],
        [Properties.settingResolve, data.resolve],
        [Properties.settingAllowEdit, data.allowEdit],
        [Properties.settingDatabaseId, data.databaseId],
        [
          Properties.settingBacklinkDisplayType,
          BacklinkDisplayTypeMap[data.backlinkDisplayType || BacklinkDisplayType.AS_LINK],
        ],
        [
          Properties.settingDisplayedDetail,
          DisplayedDetailTypeMap[data.displayedDetail || DisplayedDetailType.CREATED_AT],
        ],
        [Properties.settingFieldId, data.fieldId],
        [Properties.settingContentField, data.contentField],
        [Properties.settingIncludeVersionComment, data.includeVersionComment],
        [Properties.settingCreatable, data.creatable],
        [Properties.settingEntryFieldField, data.entryFieldField],
        [Properties.settingPageTemplateId, data.pageTemplateId],
        [Properties.settingParentPage, data.parentPage],
        [Properties.settingParentPageType, data.parentPageType],
        [Properties.settingParentPageFieldId, data.parentPageFieldId],
        [Properties.settingJiraField, data.jiraField],
        [Properties.settingJiraServerId, data.jiraServerId],
        [Properties.settingCloudId, data.cloudId],
        [
          Properties.settingSelectOptions,
          YSelectOptions.fromJSON(data.selectOptions).unwrap(),
        ],
        [Properties.settingNumberFormat, NumberFormatMap[data.numberFormat]],
        [
          Properties.settingCustomNumberFormat,
          YCustomNumberFormat.fromJSON(data.customNumberFormat).data,
        ],
        [Properties.settingDefaultEnabled, data.defaultEnabled],
      ])
    );
  }

  dispose(): void {
    this.nameElement.dispose();
    this.descriptionElement.dispose();
  }
}
